import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Calendar as CalendarIcon, Phone, ChevronRight, ChevronLeft, Check, Crown, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";
import { SITE, TIME_SLOTS } from "@/lib/site";
import SectionHeading from "@/components/SectionHeading";

const formatDate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const prettyDate = (d) =>
  d
    ? d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "";

const formatTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${period}`;
};

export default function Booking() {
  const location = useLocation();
  const [tab, setTab] = useState("full"); // "full" | "quick" | "call"
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);

  // Full flow state
  const [step, setStep] = useState(1);
  const [serviceId, setServiceId] = useState("");
  const [barberId, setBarberId] = useState(location.state?.barberId || "");
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [info, setInfo] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Quick form state
  const [quick, setQuick] = useState({ name: "", phone: "", service: "", preferred_time: "", notes: "" });
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [quickDone, setQuickDone] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/barbers").then((r) => setBarbers(r.data)).catch(() => {});
  }, []);

  const selectedService = services.find((s) => s.id === serviceId);
  const selectedBarber = barbers.find((b) => b.id === barberId);

  const canNext =
    (step === 1 && serviceId) ||
    (step === 2 && barberId) ||
    (step === 3 && date && time) ||
    (step === 4 && info.name && info.email && info.phone);

  const submitFull = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...info,
        service: selectedService?.name,
        barber: selectedBarber?.name,
        date: formatDate(date),
        time,
      };
      const { data } = await api.post("/bookings", payload);
      setConfirmed(data);
      toast.success("Your chair is reserved.");
    } catch (e) {
      toast.error("Couldn't submit booking. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuick = async (e) => {
    e.preventDefault();
    setQuickSubmitting(true);
    try {
      await api.post("/appointment-requests", quick);
      setQuickDone(true);
      toast.success("Request sent. We'll text you shortly.");
    } catch {
      toast.error("Couldn't submit. Please call us.");
    } finally {
      setQuickSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="pt-32 pb-24 px-6 md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <SectionHeading
          eyebrow="Reservations"
          title="Three ways to book your chair."
          subtitle="Plan it in detail. Send a quick request. Or just pick up the phone — we're old school like that too."
          align="center"
          testIdBase="booking-header"
        />

        {/* TABS */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-14 mb-12">
          {[
            { id: "full", label: "Full Booking", icon: <CalendarIcon className="w-4 h-4" /> },
            { id: "quick", label: "Quick Request", icon: <Mail className="w-4 h-4" /> },
            { id: "call", label: "Call to Book", icon: <Phone className="w-4 h-4" /> },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`booking-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-6 py-3.5 text-xs uppercase tracking-[0.22em] border transition-colors ${
                tab === t.id
                  ? "bg-monarch-gold text-monarch-black border-monarch-gold"
                  : "border-monarch-gold/30 text-monarch-cream hover:border-monarch-gold hover:text-monarch-gold"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* FULL BOOKING */}
        {tab === "full" && (
          <div data-testid="full-booking-panel" className="card-monarch p-6 md:p-12">
            {confirmed ? (
              <Confirmation data={confirmed} onReset={() => { setConfirmed(null); setStep(1); setServiceId(""); setBarberId(""); setDate(undefined); setTime(""); setInfo({ name: "", email: "", phone: "", notes: "" }); }} />
            ) : (
              <>
                <Stepper step={step} />

                {/* STEP 1 — Service */}
                {step === 1 && (
                  <div data-testid="booking-step-service">
                    <h3 className="font-serif text-3xl text-monarch-cream mb-2">Choose your service.</h3>
                    <p className="text-monarch-ash mb-8 text-sm">Pick the ritual. You can change it before confirming.</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {services.map((s) => (
                        <button
                          key={s.id}
                          data-testid={`select-service-${s.id}`}
                          onClick={() => setServiceId(s.id)}
                          className={`text-left p-6 border transition-all ${
                            serviceId === s.id
                              ? "border-monarch-gold bg-monarch-gold/5"
                              : "border-monarch-gold/20 hover:border-monarch-gold/50"
                          }`}
                        >
                          <div className="flex items-baseline justify-between mb-2">
                            <h4 className="font-serif text-lg text-monarch-cream">{s.name}</h4>
                          </div>
                          <p className="text-monarch-ash text-xs leading-relaxed mb-3 min-h-[2.5rem]">{s.description}</p>
                          <span className="text-[10px] uppercase tracking-[0.25em] text-monarch-ash flex items-center gap-2">
                            <Clock className="w-3 h-3" /> {s.duration}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP 2 — Barber */}
                {step === 2 && (
                  <div data-testid="booking-step-barber">
                    <h3 className="font-serif text-3xl text-monarch-cream mb-2">Pick your barber.</h3>
                    <p className="text-monarch-ash mb-8 text-sm">All royalty. All sharp. Pick a face — or the one with the best opening.</p>
                    <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
                      {barbers.map((b) => (
                        <button
                          key={b.id}
                          data-testid={`select-barber-${b.id}`}
                          onClick={() => setBarberId(b.id)}
                          className={`text-left border overflow-hidden transition-all ${
                            barberId === b.id
                              ? "border-monarch-gold ring-1 ring-monarch-gold/30"
                              : "border-monarch-gold/20 hover:border-monarch-gold/50"
                          }`}
                        >
                          <div className="img-zoom aspect-[3/4] grayscale">
                            <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="p-4">
                            <div className="text-[9px] uppercase tracking-[0.3em] text-monarch-gold mb-1">{b.role}</div>
                            <div className="font-serif text-base text-monarch-cream">{b.name}</div>
                            <div className="text-[11px] text-monarch-ash mt-1">{b.specialty}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP 3 — Date & Time */}
                {step === 3 && (
                  <div data-testid="booking-step-datetime">
                    <h3 className="font-serif text-3xl text-monarch-cream mb-2">Pick a date & time.</h3>
                    <p className="text-monarch-ash mb-8 text-sm">All times in shop local time.</p>
                    <div className="grid lg:grid-cols-2 gap-10">
                      <div data-testid="booking-calendar" className="border border-monarch-gold/20 p-4 bg-monarch-coal/40">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => {
                            const today = new Date(); today.setHours(0, 0, 0, 0);
                            return d < today;
                          }}
                          className="text-monarch-cream"
                        />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-monarch-gold mb-4">Available Times</div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                          {TIME_SLOTS.map((t) => (
                            <button
                              key={t}
                              data-testid={`time-slot-${t}`}
                              disabled={!date}
                              onClick={() => setTime(t)}
                              className={`py-2.5 text-xs tracking-wider border transition-colors ${
                                time === t
                                  ? "bg-monarch-gold text-monarch-black border-monarch-gold"
                                  : "border-monarch-gold/20 text-monarch-cream hover:border-monarch-gold disabled:opacity-30 disabled:cursor-not-allowed"
                              }`}
                            >
                              {formatTime(t)}
                            </button>
                          ))}
                        </div>
                        {!date && <p className="text-monarch-ash text-xs mt-4">Pick a date to unlock times.</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4 — Details */}
                {step === 4 && (
                  <div data-testid="booking-step-details">
                    <h3 className="font-serif text-3xl text-monarch-cream mb-2">Your details.</h3>
                    <p className="text-monarch-ash mb-8 text-sm">We&rsquo;ll text and email you a confirmation.</p>
                    <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                      <Field label="Full Name" testId="booking-name">
                        <input data-testid="booking-input-name" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} className="input-monarch" placeholder="Your name" />
                      </Field>
                      <Field label="Phone" testId="booking-phone">
                        <input data-testid="booking-input-phone" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} className="input-monarch" placeholder="(555) 123-4567" />
                      </Field>
                      <Field label="Email" testId="booking-email" className="md:col-span-2">
                        <input data-testid="booking-input-email" type="email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} className="input-monarch" placeholder="you@example.com" />
                      </Field>
                      <Field label="Notes for your barber (optional)" testId="booking-notes" className="md:col-span-2">
                        <textarea data-testid="booking-input-notes" rows={3} value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} className="input-monarch resize-none" placeholder="Anything we should know?" />
                      </Field>
                    </div>

                    <Summary service={selectedService} barber={selectedBarber} date={date} time={time} />
                  </div>
                )}

                {/* NAV */}
                <div className="flex justify-between items-center mt-12 pt-8 border-t border-monarch-gold/15">
                  <button
                    data-testid="booking-prev"
                    onClick={() => setStep((s) => Math.max(1, s - 1))}
                    disabled={step === 1}
                    className="btn-outline-gold disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  {step < 4 ? (
                    <button
                      data-testid="booking-next"
                      disabled={!canNext}
                      onClick={() => setStep((s) => Math.min(4, s + 1))}
                      className="btn-gold disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Continue <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      data-testid="booking-submit"
                      disabled={!canNext || submitting}
                      onClick={submitFull}
                      className="btn-gold disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Reserving…" : "Confirm Booking"} <Crown className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* QUICK REQUEST */}
        {tab === "quick" && (
          <div data-testid="quick-booking-panel" className="card-monarch p-6 md:p-12 max-w-3xl mx-auto">
            {quickDone ? (
              <div className="text-center py-10" data-testid="quick-success">
                <Check className="w-14 h-14 text-monarch-gold mx-auto mb-5" />
                <h3 className="font-serif text-3xl text-monarch-cream mb-3">Request received.</h3>
                <p className="text-monarch-ash">A barber will text you within an hour to confirm your slot.</p>
                <button data-testid="quick-reset" onClick={() => { setQuick({ name: "", phone: "", service: "", preferred_time: "", notes: "" }); setQuickDone(false); }} className="btn-outline-gold mt-8">Send Another</button>
              </div>
            ) : (
              <form onSubmit={submitQuick} className="space-y-7">
                <div>
                  <h3 className="font-serif text-3xl text-monarch-cream mb-2">Send a quick request.</h3>
                  <p className="text-monarch-ash text-sm">No calendar. No fuss. Tell us when works and we&rsquo;ll lock it in.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                  <Field label="Name" testId="quick-name">
                    <input data-testid="quick-input-name" required value={quick.name} onChange={(e) => setQuick({ ...quick, name: e.target.value })} className="input-monarch" />
                  </Field>
                  <Field label="Phone" testId="quick-phone">
                    <input data-testid="quick-input-phone" required value={quick.phone} onChange={(e) => setQuick({ ...quick, phone: e.target.value })} className="input-monarch" />
                  </Field>
                  <Field label="Service" testId="quick-service">
                    <select data-testid="quick-input-service" value={quick.service} onChange={(e) => setQuick({ ...quick, service: e.target.value })} className="input-monarch bg-monarch-ink">
                      <option value="">— Select a service —</option>
                      {services.map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}
                    </select>
                  </Field>
                  <Field label="Preferred Time" testId="quick-time">
                    <input data-testid="quick-input-time" value={quick.preferred_time} onChange={(e) => setQuick({ ...quick, preferred_time: e.target.value })} className="input-monarch" placeholder="e.g. Sat afternoon" />
                  </Field>
                  <Field label="Notes (optional)" testId="quick-notes" className="md:col-span-2">
                    <textarea data-testid="quick-input-notes" rows={3} value={quick.notes} onChange={(e) => setQuick({ ...quick, notes: e.target.value })} className="input-monarch resize-none" />
                  </Field>
                </div>
                <button data-testid="quick-submit" disabled={quickSubmitting} className="btn-gold">
                  {quickSubmitting ? "Sending…" : "Send Request"} <ChevronRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        )}

        {/* CALL TO BOOK */}
        {tab === "call" && (
          <div data-testid="call-booking-panel" className="card-monarch p-10 md:p-16 max-w-3xl mx-auto text-center">
            <Phone className="w-14 h-14 text-monarch-gold mx-auto mb-6" strokeWidth={1.4} />
            <h3 className="font-serif text-3xl md:text-4xl text-monarch-cream mb-4">Old-school works too.</h3>
            <p className="text-monarch-ash leading-relaxed max-w-md mx-auto">Pick up the phone. A real human will pick up the other side. Walk-ins also welcome — but a call gets you in faster.</p>
            <a href={SITE.phoneHref} data-testid="call-now-button" className="btn-gold mt-10 text-base">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
            <div className="mt-12 pt-8 border-t border-monarch-gold/15 grid md:grid-cols-2 gap-6 text-left">
              <div>
                <div className="eyebrow mb-3">Hours</div>
                <ul className="text-sm space-y-1.5">
                  {SITE.hours.map((h) => (
                    <li key={h.day} className="flex justify-between text-monarch-cream/80">
                      <span>{h.day}</span><span className="text-monarch-ash">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="eyebrow mb-3">Visit</div>
                <p className="text-sm text-monarch-cream/80 leading-relaxed">{SITE.address}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ step }) {
  const steps = ["Service", "Barber", "Date & Time", "Details"];
  return (
    <div className="flex items-center gap-3 md:gap-6 mb-10 overflow-x-auto pb-2">
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < step;
        const active = n === step;
        return (
          <div key={label} className="flex items-center gap-3 md:gap-6 shrink-0">
            <div className="flex items-center gap-3">
              <div
                data-testid={`stepper-${n}`}
                className={`w-9 h-9 border flex items-center justify-center text-xs font-serif transition-colors ${
                  done
                    ? "bg-monarch-gold border-monarch-gold text-monarch-black"
                    : active
                    ? "border-monarch-gold text-monarch-gold"
                    : "border-monarch-gold/25 text-monarch-ash"
                }`}
              >
                {done ? <Check className="w-4 h-4" /> : n}
              </div>
              <span className={`text-[11px] uppercase tracking-[0.25em] hidden md:inline ${active || done ? "text-monarch-cream" : "text-monarch-ash"}`}>{label}</span>
            </div>
            {i < steps.length - 1 && <div className="w-6 md:w-10 h-px bg-monarch-gold/20" />}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children, className = "", testId }) {
  return (
    <label className={`block ${className}`} data-testid={testId ? `${testId}-field` : undefined}>
      <span className="text-[10px] uppercase tracking-[0.3em] text-monarch-gold mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function Summary({ service, barber, date, time }) {
  return (
    <div data-testid="booking-summary" className="mt-10 border border-monarch-gold/20 p-6 bg-monarch-coal/40">
      <div className="eyebrow mb-4">Your Reservation</div>
      <div className="grid md:grid-cols-4 gap-5 text-sm">
        <SumCell label="Service" value={service?.name} sub={service ? `${service.duration}` : ""} />
        <SumCell label="Barber" value={barber?.name} sub={barber?.role} />
        <SumCell label="Date" value={prettyDate(date)} />
        <SumCell label="Time" value={time ? formatTime(time) : ""} />
      </div>
    </div>
  );
}

function SumCell({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-monarch-ash mb-1">{label}</div>
      <div className="text-monarch-cream font-serif text-base">{value || "—"}</div>
      {sub && <div className="text-xs text-monarch-ash mt-1">{sub}</div>}
    </div>
  );
}

function Confirmation({ data, onReset }) {
  return (
    <div className="text-center py-14" data-testid="booking-confirmation">
      <div className="w-16 h-16 border border-monarch-gold mx-auto flex items-center justify-center mb-7">
        <Crown className="w-7 h-7 text-monarch-gold" />
      </div>
      <h3 className="font-serif text-3xl md:text-4xl text-monarch-cream mb-3">Your chair is reserved.</h3>
      <p className="text-monarch-ash max-w-md mx-auto leading-relaxed">A confirmation has been sent to {data.email}. We&rsquo;ll see you on {data.date} at {formatTime(data.time)}.</p>

      <div className="mt-10 max-w-md mx-auto text-left border border-monarch-gold/20 p-6">
        <SumCell label="Reservation #" value={data.id.slice(0, 8).toUpperCase()} />
        <div className="mt-4 grid grid-cols-2 gap-4">
          <SumCell label="Service" value={data.service} />
          <SumCell label="Barber" value={data.barber} />
          <SumCell label="Date" value={data.date} />
          <SumCell label="Time" value={formatTime(data.time)} />
        </div>
      </div>
      <button data-testid="booking-reset" onClick={onReset} className="btn-outline-gold mt-10">Book another chair</button>
    </div>
  );
}
